import { registerSW } from "virtual:pwa-register";
// **Default import, e nao `import * as`.** O pacote publica ESM em
// `dist/qrcode.mjs` com `export default`, mas os tipos em `dist/qrcode.d.ts`
// ainda descrevem `export =`. O `allowSyntheticDefaultImports` do tsconfig
// cobre a diferenca; um namespace import passaria no `tsc` e **quebraria em
// runtime** com `Cannot call a namespace`.
import qrcode from "qrcode-generator";
import {
  ErroDaApi,
  aceitarConvite,
  confirmarTotp,
  entrar,
  iniciarTotp,
  recuperar,
  sair,
  totpPendente,
} from "./api";
import { aoMudar, caminhoAtual, comecar, trocar } from "./roteador";
import * as sessao from "./sessao";
import * as telaDeAparelhos from "./aparelhos";
import * as telaDeCentral from "./central";
import * as telaDoMapa from "./mapa";
import {
  painel,
  telaDaCasa,
  telaDeCodigos,
  telaDeConvite,
  telaDeEntrada,
  telaDeFalha,
  telaDeRecuperacao,
  telaDeTotp,
  telaSemConexao,
} from "./telas";
import type { Usuario } from "./tipos";
import "./estilo.css";

/**
 * Os codigos de recuperacao da confirmacao de TOTP mais recente.
 *
 * **Sao mostrados uma unica vez**: a API os gera e nao tem como recapturar. A
 * tela os desenha da memoria, e nao do servidor -- pedi-los de novo apagaria os
 * dez. O storage e so de sessao (um refresh perde os codigos), e a rota
 * `/codigo` e o unico lugar que os le: a tela de setup nao redesenha eles, e
 * muito menos os regenera.
 */
let codigosDeRecuperacao: { codigos: string[]; aviso: string } | null = null;

const CASCA = document.querySelector<HTMLElement>("#casca");
const CONTEUDO = document.querySelector<HTMLElement>("#conteudo");
const ESTADO = document.querySelector<HTMLElement>("#estado-do-servico");

// --------------------------------------------------------------- service worker

/**
 * `data-sw` e o que o teste de integracao da casca le. `ativo` so quando
 * `navigator.serviceWorker.ready` resolve -- que e o navegador dizendo que ha um
 * worker controlando a pagina, e nao um booleano que este codigo decide.
 */
function estadoDoServico(valor: string, rotulo: string): void {
  if (ESTADO === null) {
    return;
  }
  ESTADO.dataset["sw"] = valor;
  ESTADO.textContent = rotulo;
}

if (!("serviceWorker" in navigator)) {
  estadoDoServico("sem-suporte", "sem service worker");
} else {
  estadoDoServico("nao-registrado", "instalando...");
  registerSW({
    immediate: true,
    onRegisterError(erro) {
      estadoDoServico("falhou", `falhou: ${String(erro)}`);
    },
  });
  void navigator.serviceWorker.ready.then(() => estadoDoServico("ativo", "instalada"));
}

// -------------------------------------------------------------------- desenho

function desenha(html: string, rota: string): void {
  if (CONTEUDO === null) {
    return;
  }
  // **Toda tela entra no `painel`**, e nao so as de acesso: a largura limitada e
  // o que impede a tela de login de esticar num tablet.
  CONTEUDO.innerHTML = painel(html);
  CONTEUDO.classList.add("conteudo--centrado");
  CASCA?.setAttribute("data-rota", rota);
}

/**
 * **Substitui a tela inteira quando o foco esta num campo de senha.**
 *
 * `replaceState` em mudanca de sessao ja resolve o botao "voltar". Este e o outro
 * lado: trocar `input` por texto e deixar `SenhaDoTeste123` no historico de
 * ScriptBackForwardCache, um lugar que ninguem lembra de limpar.
 */
function redesenha(): void {
  void renderizar(caminhoAtual());
}

/** Le `id`, e lanca se o elemento nao existir -- layout e teste nao podem divergir. */
function campo(id: string): HTMLInputElement {
  const elemento = document.querySelector<HTMLInputElement>(`#${id}`);
  if (elemento === null) {
    throw new Error(`a tela nao tem o campo #${id}`);
  }
  return elemento;
}

function mostraErro(mensagem: string | null): void {
  const alvo = document.querySelector<HTMLElement>("#erro");
  if (alvo === null) {
    return;
  }
  if (mensagem === null) {
    alvo.hidden = true;
    alvo.textContent = "";
    return;
  }
  alvo.hidden = false;
  alvo.textContent = mensagem;
}

/**
 * Liga o envio de um formulario: bloqueia o botao, mostra o erro e **limpa a
 * senha depois**, bem-sucedida ou nao.
 *
 * **Os elementos sao guardados como referencia, e nao procurados de novo.** O
 * `aoEnviar` pode trocar o `innerHTML` do formulario -- o login faz isso, porque
 * definir a sessao redesenha a tela. Depois disso, `document.querySelector("#senha")`
 * devolve `null` e `campo("senha")` estoura: o no **desligado do documento** ainda
 * e um objeto valido e continua aceitando `value = ""`.
 */
function liga(aoEnviar: () => Promise<string>): void {
  const forma = document.querySelector<HTMLFormElement>("#forma");
  const botao = document.querySelector<HTMLButtonElement>("#enviar");
  if (forma === null || botao === null) {
    return;
  }
  const senha = document.querySelector<HTMLInputElement>("#senha");
  const limparSenha = botao.dataset["limparSenha"] === "sim";

  forma.addEventListener("submit", (evento) => {
    evento.preventDefault();
    mostraErro(null);
    // **O botao desabilita durante o envio.** Sem isso, dois toques em succession
    // mandam dois logins, e o rate limit da API (5 por 15 min) conta os dois --
    // a pessoa toma conta de um limite que ela nao gastou.
    botao.disabled = true;
    CASCA?.setAttribute("data-enviando", "sim");

    void aoEnviar()
      .then((destino) => {
        if (limparSenha && senha !== null) {
          senha.value = "";
        }
        if (destino !== "") {
          trocar(destino);
        } else {
          redesenha();
        }
      })
      .catch((erro: unknown) => {
        if (erro instanceof ErroDaApi) {
          // **O 429 diz quando voltar**, e o `Retry-After` vem da API em segundos.
          // Mostrar "tente de novo" sem o tempo faz a pessoa repetir, e repetir
          // consome mais tentativas do limite que ela ja esta perto de bater.
          mostraErro(
            erro.esperarSegundos === null
              ? erro.message
              : `${erro.message} Tente de novo em ${erro.esperarSegundos}s.`,
          );
        } else {
          mostraErro("Algo deu errado. Tente de novo.");
        }
        botao.disabled = false;
        CASCA?.setAttribute("data-enviando", "nao");
      });
  });
}

/**
 * O QR do `otpauth://`, como `data:` URL para um `<img>`.
 *
 * **Nivel de correcao `M`.** A tela e lida por uma camera de celular, a uns
 * 20 cm de distancia: o `L` falha com reflexo na tela, e o `H` infla o codigo
 * sem ganho nessa distancia.
 *
 * O tipo `0` e o "menor que couber": a `uri` tem tamanho previsivel, e fixar a
 * versao a mao so criaria um numero para manter em dia.
 */
function qrDe(uri: string): string {
  const codigo = qrcode(0, "M");
  codigo.addData(uri);
  codigo.make();
  return codigo.createDataURL(6, 2);
}

function aposEntrar(usuario: Usuario): string {
  // **O admin sem TOTP vai para o setup antes de qualquer outra coisa.** A API
  // marca `precisa_configurar_totp`, e a tela do autenticador e mais uma barreira
  // que pode ser pulada indo direto para a rota seguinte.
  return usuario.is_admin && usuario.precisa_configurar_totp ? "/totp" : "/";
}

/**
 * Desenha a rota, **e trata o que o desenho lancar**.
 *
 * **Uma tela que falha nao pode deixar o esqueleto do `index.html` na tela.** O
 * desenho chama a API, e a API pode recusar; sem este `catch`, a excecao subia
 * como rejeicao nao tratada e o que ficava visivel era o texto de carregamento
 * do HTML -- que parece app que nao abriu, e nao diz nada sobre a causa.
 *
 * Dois casos, e so um deles tem destino:
 *
 * - **o segundo fator esta pendente** (`403` com `X-precisa-configurar-totp`): a
 *   API bloqueia tudo menos o setup, entao o lugar da pessoa e `/totp`. Sem
 *   isto, quem ainda nao confirmou o autenticador via uma tela morta em
 *   qualquer rota privada -- e a PWA instalada abre em `/`, nao no setup, entao
 *   era esse o caminho normal de quem acabou de instalar.
 * - **qualquer outra falha**: a tela de falha, com a mensagem da API.
 */
async function renderizar(caminho: string): Promise<void> {
  try {
    await desenharRota(caminho);
  } catch (erro) {
    // **A guarda contra o laco:** `/totp` e o destino, e nao a origem. Se for
    // ele que falhar, redirecionar para ele de novo nao sairia do lugar.
    if (erro instanceof ErroDaApi && erro.precisaConfigurarTotp && caminho !== "/totp") {
      trocar("/totp");
      return;
    }
    desenha(telaDeFalha(erro instanceof ErroDaApi ? erro.message : "Algo deu errado."), "falha");
  }
}

/**
 * O desenho de uma rota, com **a guarda de rota privada em um lugar so**.
 *
 * Nao chame daqui de fora: quem navega passa pelo `renderizar`, que e quem trata
 * o que esta funcao lanca.
 */
async function desenharRota(caminho: string): Promise<void> {
  if (sessao.sessaoAtual() === "carregando") {
    desenha(`<p class="vazio">Abrindo a casa...</p>`, "carregando");
    return;
  }

  // **A casa fora do ar tem tela propria.** Sem isto, uma rede ruim mostrava a
  // tela de acesso: a pessoa achava que tinha sido desconectada e digitava a
  // senha -- num proxy captive, para quem estivesse no meio.
  if (sessao.sessaoAtual() === "sem-conexao") {
    desenha(telaSemConexao(), "sem-conexao");
    const tentar = document.querySelector<HTMLButtonElement>("#tentar-de-novo");
    tentar?.addEventListener("click", () => {
      sessao.definir("carregando");
      void sessao.carregar().then(() => renderizar(caminhoAtual()));
    });
    return;
  }

  const autenticado = sessao.sessaoAtual() === "autenticado";
  const usuario = sessao.usuarioAtual();

  // Convite e recuperacao sao os unicos caminhos que funcionam sem sessao --
  // e sao justamente os que **criam** uma.
  if (caminho.startsWith("/convite/")) {
    if (autenticado) {
      trocar("/");
      return;
    }
    const token = caminho.slice("/convite/".length);
    desenha(telaDeConvite(token), "convite");
    liga(async () => {
      const conta = await aceitarConvite(token, campo("nome").value, campo("senha").value);
      sessao.definir("autenticado", conta, false);
      return aposEntrar(conta);
    });
    return;
  }

  if (caminho === "/recuperar") {
    desenha(telaDeRecuperacao(), "recuperar");
    liga(async () => {
      const conta = await recuperar(campo("email").value, campo("senha").value, campo("codigo").value);
      sessao.definir("autenticado", conta, false);
      // Recuperar reinicia o TOTP, entao o proximo passo e configurar de novo.
      return conta.is_admin ? "/totp" : "/";
    });
    return;
  }

  if (caminho === "/entrar") {
    if (autenticado) {
      trocar("/");
      return;
    }
    desenha(telaDeEntrada(), "entrar");
    const enviar = document.querySelector<HTMLButtonElement>("#enviar");
    if (enviar !== null) {
      enviar.dataset["limparSenha"] = "sim";
    }
    liga(async () => {
      const codigo = campo("codigo").value.trim();
      const conta = await entrar(
        campo("email").value,
        campo("senha").value,
        codigo === "" ? null : codigo,
      );
      sessao.definir("autenticado", conta, false);
      return aposEntrar(conta);
    });
    return;
  }

  if (caminho === "/alertas") {
    if (!autenticado || usuario === null) {
      trocar("/entrar");
      return;
    }
    await telaDeCentral.desenhar();
    // **A stream abre uma vez, e nao a cada redesenho** -- cada stream segura
    // uma conexao do pool da API.
    telaDeCentral.abrir();
    CASCA?.setAttribute("data-rota", "alertas");
    return;
  }

  // **Sair da central fecha a stream.** Cada stream seguraria uma sessao do
  // pool da API enquanto a aba estivesse aberta, mesmo com a pessoa em outra
  // tela -- e a API ficaria sem conexao para atender anybody.
  telaDeCentral.fechar();
  // **O mapa fecha junto.** Alem da stream, ele segura um mapa do Leaflet vivo
  // sobre o DOM: sem o `remove()`, o proximo `innerHTML` deixaria o Leaflet
  // apontando para um elemento que nao existe mais.
  telaDoMapa.fechar();

  if (caminho === "/mapa") {
    if (!autenticado || usuario === null) {
      trocar("/entrar");
      return;
    }
    // **A rota do mapa e a mesma da central:** desenhada pelo modulo, porque o
    // mapa precisa do container `#mapa` intacto entre uma pintura e outra, e o
    // `desenha` comum substitui a tela inteira.
    await telaDoMapa.desenhar();
    telaDoMapa.abrir();
    CASCA?.setAttribute("data-rota", "mapa");
    return;
  }

  if (caminho === "/aparelhos") {
    if (!autenticado || usuario === null) {
      trocar("/entrar");
      return;
    }
    // **A tela de Aparelhos e a que mexe em credencial**, entao e a que mais
    // depende de a sessao estar viva: ela e desenhada por conta propria, e nao
    // pelo esqueleto da casa.
    await telaDeAparelhos.desenhar();
    // A rota e marcada aqui, e nao dentro do modulo: `data-rota` e o que o
    // teste le para saber em que tela esta, e ele precisa valer para **todas**
    // as telas -- inclusive as que nao passam pelo `desenha` comum.
    CASCA?.setAttribute("data-rota", "aparelhos");
    return;
  }

  if (caminho === "/totp") {
    if (!autenticado || usuario === null) {
      trocar("/entrar");
      return;
    }
    // **O setup pendente vem antes de um novo.** `iniciarTotp` regenera o
    // segredo, e chama-lo a cada desenho da tela invalidava o que a pessoa
    // acabou de cadastrar no autenticador: no celular, sair para o app do
    // autenticador e voltar recarrega a aba, e o codigo que ele mostrava
    // passava a dar `400` sem nada apontando a causa. O `POST` continua no
    // caminho, para quando nao ha setup nenhum para retomar.
    const paraConfigurar = (await totpPendente()) ?? (await iniciarTotp());
    // **O segredo e buscado no servidor e nunca fica no endereco.** Ele aparece
    // na URL, entra no historico do navegador e vai para o log do proxy.
    desenha(telaDeTotp(paraConfigurar.segredo, qrDe(paraConfigurar.uri)), "totp");
    liga(async () => {
      const confirmados = await confirmarTotp(campo("codigo").value.trim());
      codigosDeRecuperacao = confirmados;
      // **Atualiza o usuario sem redesenhar.** Ele ainda carrega
      // `precisa_configurar_totp` do login; sem isso a casa mostra "pendente"
      // mesmo depois de tudo confirmado. `carregar()` publica e redesenha a rota
      // atual -- aqui, `/totp` -- e ainda que nada regenerasse, manter o refresh
      // nele deixa a atualizacao longe de onde o estado importa. Com
      // `publicar = false` ele atualiza a memoria sem virar no laco: a proxima
      // navegacao le o valor fresco.
      await sessao.recarregarUsuario();
      // **A tela de codigos e o destino, e nao `/totp` de novo.** Redesenhar
      // `/totp` apos confirmar faz `totpPendente()` dar 404 e `iniciarTotp()`
      // regenerar o segredo -- invalidando o que a pessoa acabou de cadastrar no
      // autenticador, e a tela "pisca" entre os codigos e o setup de novo.
      return "/codigo";
    });
    return;
  }

  // **Os codigos de recuperacao so aparecem uma vez, aqui.** E o destino da
  // confirmacao: ninguem navega ate `/codigo` de memoria, e a tela de setup nao
  // redesenha ela -- e e exatamente o redesenho que regenerava o segredo.
  if (caminho === "/codigo") {
    if (!autenticado || usuario === null) {
      trocar("/entrar");
      return;
    }
    if (codigosDeRecuperacao === null) {
      trocar("/");
      return;
    }
    desenha(telaDeCodigos(codigosDeRecuperacao.codigos, codigosDeRecuperacao.aviso), "codigos");
    return;
  }

  if (caminho === "/sair") {
    await sair();
    sessao.definir("anonimo", null, false);
    trocar("/entrar");
    return;
  }

  if (!autenticado || usuario === null) {
    trocar("/entrar");
    return;
  }

  desenha(telaDaCasa(usuario.nome, usuario.is_admin, usuario.precisa_configurar_totp), "casa");
}

// ------------------------------------------------------------------------ boot

/**
 * **O boot e assincrono e o desenho espera.** A sessao esta num cookie
 * `HttpOnly`: o JavaScript nao tem como saber quem e sem perguntar. Desenhar a
 * tela de acesso primeiro e trocar depois produziria um piscar de "nao tem
 * sessao" em toda abertura de casa -- e pior, numShared tablet, pareceria que a
 * pessoa foi desconectada.
 */
async function iniciar(): Promise<void> {
  comecar();
  // A tela de Aparelhos desenha no mesmo elemento do resto, e por isso precisa
  // saber qual e.
  if (CONTEUDO !== null) {
    telaDeAparelhos.definirRaiz(CONTEUDO);
    telaDeCentral.definirRaiz(CONTEUDO);
    telaDoMapa.definirRaiz(CONTEUDO);
  }
  // A guarda contra rota privada fica **em um lugar so**, `renderizar`: quem
  // navega dispara o redesenho pelo roteador, e o redesenho decide. Se cada tela
  // checasse sessao, uma rota nova nasceria sem guarda.
  aoMudar(() => {
    void renderizar(caminhoAtual());
  });

  await sessao.carregar();
  await renderizar(caminhoAtual());
  sessao.observar(() => {
    void renderizar(caminhoAtual());
  });

  CASCA?.setAttribute("data-pronto", "sim");
}

void iniciar();